import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, serverTimestamp, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { MultiSelect, MultiSelectOption } from '@/components/ui/multi-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Header from '@/components/layout/Header';
import { WideDashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { Send, Bell, Users, Clock, CheckCircle, Circle, Eye, Mail, ListChecks } from 'lucide-react';
import { showSuccess, showError, confirmAction } from '@/lib/swal';
import { useAuth } from '@/contexts/AuthContext';
import { Announcement, RSVP, Unit, RecipientGroup, AnnouncementRecipient, Event, GuestListEntry } from '@/types';
import { formatDate } from '@/lib/utils';
import { mapEventData, shouldIncludeRsvpInUnpaidAnnouncementGroup } from '@/lib/event-utils';
import { EventHeaderCredits } from '@/components/event/EventHeaderCredits';
import {
  buildAnnouncementRsvpUrl,
  buildAnnouncementRsvpDashboardUrl,
  buildDefaultAnnouncementSubject,
  formatEventDateForAnnouncementEmail,
  getPaymentPreviewSample,
  getPreviewExampleRecipient,
} from '@/lib/announcement-email-preview';
import { AnnouncementEmailPreview } from '@/components/event/AnnouncementEmailPreview';
import {
  buildAnnouncementRecipientsList,
  recipientSourceLabel,
} from '@/lib/announcement-recipients-list';
import {
  sendAnnouncementInBatches,
  type SendAnnouncementEmailPayload,
  type SendAnnouncementEmailResult,
  type SendAnnouncementProgress,
} from '@/lib/send-announcement-batches';

const parseDate = (value: unknown): Date | undefined => {
  if (!value) {
    return undefined;
  }

  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  const parsed = new Date(value as string | number | Date);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export default function AnnouncementsPage() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<SendAnnouncementProgress | null>(null);
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [event, setEvent] = useState<Event | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [unit, setUnit] = useState<Unit | null>(null);
  const [subscribedGuestsCount, setSubscribedGuestsCount] = useState(0);
  const [unitMembersCount, setUnitMembersCount] = useState(0);
  
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [sendTo, setSendTo] = useState<RecipientGroup[]>([]);
  const [includeRsvpLink, setIncludeRsvpLink] = useState(false);
  const [includeRsvpDashboardLink, setIncludeRsvpDashboardLink] = useState(false);
  const [includePaymentLink, setIncludePaymentLink] = useState(false);
  
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [recipients, setRecipients] = useState<AnnouncementRecipient[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [showRecipientsDialog, setShowRecipientsDialog] = useState(false);
  const [invoiceById, setInvoiceById] = useState<Map<string, { status?: string; outstandingBalance?: number }>>(
    () => new Map()
  );
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showRecipientListDialog, setShowRecipientListDialog] = useState(false);
  const [guestList, setGuestList] = useState<GuestListEntry[]>([]);
  const [unitMemberDirectory, setUnitMemberDirectory] = useState<{ email: string; name: string }[]>([]);
  const [unitMembersDirectoryLoading, setUnitMembersDirectoryLoading] = useState(false);

  const memberUidsKey = useMemo(() => unit?.members?.map((m) => m.uid).join('|') ?? '', [unit?.members]);

  const defaultSubjectLine = useMemo(
    () => buildDefaultAnnouncementSubject(event?.name),
    [event?.name]
  );

  useEffect(() => {
    setSubject(defaultSubjectLine);
  }, [defaultSubjectLine]);

  useEffect(() => {
    const fetchData = async () => {
      if (!unitId || !eventId) {
        setLoading(false);
        return;
      }

      try {
        // Fetch Unit data
        const unitDoc = await getDoc(doc(db, 'units', unitId));
        if (unitDoc.exists()) {
          const unitData = unitDoc.data();
          const fetchedUnit: Unit = {
            id: unitDoc.id,
            name: unitData.name,
            unitNumber: unitData.unitNumber?.toString() || unitData.number?.toString() || '',
            typeId: unitData.typeId || 'default-type',
            createdAt: parseDate(unitData.createdAt) || new Date(),
            members: unitData.members || [],
            settings: unitData.settings || {},
            subscription: unitData.subscription || { status: 'trial', billingEmail: '' },
          };
          setUnit(fetchedUnit);
          setSubscribedGuestsCount(unitData.guestList?.length || 0);
          setUnitMembersCount(unitData.members?.length || 0);

          const rawGuests = unitData.guestList;
          if (Array.isArray(rawGuests)) {
            setGuestList(
              rawGuests.map((g: Record<string, unknown>, i: number) => ({
                id: typeof g.id === 'string' ? g.id : `guest-${i}`,
                email: String(g.email || ''),
                firstName: typeof g.firstName === 'string' ? g.firstName : undefined,
                lastName: typeof g.lastName === 'string' ? g.lastName : undefined,
                name: typeof g.name === 'string' ? g.name : undefined,
                addedAt: parseDate(g.addedAt) || new Date(),
                source: g.source === 'rsvp' || g.source === 'manual' ? g.source : 'manual',
                userId: typeof g.userId === 'string' ? g.userId : undefined,
              }))
            );
          } else {
            setGuestList([]);
          }
        }

        // Fetch RSVPs
        const rsvpsSnapshot = await getDocs(
          collection(db, 'units', unitId, 'events', eventId, 'rsvps')
        );
        const rsvpData: RSVP[] = [];
        rsvpsSnapshot.forEach((doc) => {
          const data = doc.data();
          rsvpData.push({
            id: doc.id,
            eventId,
            userId: data.userId,
            name: data.name,
            rank: data.rank,
            unit: data.unit,
            email: data.email,
            responses: data.responses,
            status: data.status,
            invoiceId: data.invoiceId,
            paid: data.paid,
            invitedBy: data.invitedBy,
            checkedIn: data.checkedIn,
            checkInTime: parseDate(data.checkInTime),
            specialRequests: data.specialRequests,
            plusOneAllowed: data.plusOneAllowed,
            createdAt: parseDate(data.createdAt) || new Date(),
            updatedAt: parseDate(data.updatedAt) || new Date(),
          });
        });
        setRsvps(rsvpData);

        const invoicesSnapshot = await getDocs(
          collection(db, 'units', unitId, 'events', eventId, 'invoices')
        );
        const invMap = new Map<string, { status?: string; outstandingBalance?: number }>();
        invoicesSnapshot.forEach((invDoc) => {
          const data = invDoc.data();
          invMap.set(invDoc.id, {
            status: data.status,
            outstandingBalance: data.outstandingBalance,
          });
        });
        setInvoiceById(invMap);

        // Fetch event (for unpaid count / ticket pricing)
        const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
        if (eventDoc.exists()) {
          setEvent(mapEventData(eventDoc, unitId));
        }

        // Fetch announcements
        const announcementsQuery = query(
          collection(db, 'units', unitId, 'events', eventId, 'announcements'),
          orderBy('createdAt', 'desc')
        );
        const announcementsSnapshot = await getDocs(announcementsQuery);
        const announcementData: Announcement[] = [];
        announcementsSnapshot.forEach((doc) => {
          const data = doc.data();
          announcementData.push({
            id: doc.id,
            eventId,
            message: data.message,
            createdBy: data.createdBy,
            createdAt: parseDate(data.createdAt) || new Date(),
            sentTo: data.sentTo,
            readBy: data.readBy || [],
            recipientCount: data.recipientCount,
          });
        });
        setAnnouncements(announcementData);
      } catch (error) {
        console.error('Error fetching data:', error);
        showError('Failed to load announcements');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [unitId, eventId]);

  useEffect(() => {
    if (!unitId || !memberUidsKey || !unit?.members?.length) {
      setUnitMemberDirectory([]);
      setUnitMembersDirectoryLoading(false);
      return;
    }

    let cancelled = false;
    setUnitMembersDirectoryLoading(true);

    (async () => {
      try {
        // Resolved server-side (Admin SDK) via the same callable the Settings >
        // Members tab uses, rather than reading users/{uid} docs directly from
        // the client: Firestore rules only allow a user to read their own
        // profile, so a client-side loop would silently skip every other
        // member and undercount this group versus what actually gets sent.
        const getUnitMemberProfiles = httpsCallable(functions, 'getUnitMemberProfiles');
        const result = await getUnitMemberProfiles({ unitId });
        const members =
          (result.data as { members?: { email?: string; name?: string }[] })?.members || [];
        const recipients = members
          .filter((m): m is { email: string; name?: string } => !!m.email && m.email.includes('@'))
          .map((m) => ({ email: m.email, name: m.name || 'Member' }));
        if (!cancelled) {
          setUnitMemberDirectory(recipients);
        }
      } catch (error) {
        console.error('Error resolving unit member emails:', error);
        if (!cancelled) {
          setUnitMemberDirectory([]);
        }
      } finally {
        if (!cancelled) {
          setUnitMembersDirectoryLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [unitId, memberUidsKey, unit?.members]);

  // Delegates to the same deduplicated recipient list shown in the "Recipients
  // for this send" preview (see pendingRecipientsList below), so the count
  // used for the send button label, confirmation dialog, and progress bar can
  // never drift from what the preview shows the organiser.
  const getRecipientCount = () => pendingRecipientsList.length;

  const recipientOptions: MultiSelectOption[] = [
    {
      value: 'all',
      label: 'All Attendees',
      count: rsvps.filter((r) => r.status !== 'cancelled' && r.status !== 'declined').length,
    },
    { value: 'confirmed', label: 'Confirmed Only', count: rsvps.filter((r) => r.status === 'confirmed').length },
    { value: 'waitlist', label: 'Registered Interest', count: rsvps.filter((r) => r.status === 'waitlist').length },
    {
      value: 'unpaid',
      label: 'Unpaid',
      count: rsvps.filter((r) => shouldIncludeRsvpInUnpaidAnnouncementGroup(r, event, invoiceById)).length,
    },
    { value: 'subscribers', label: 'Subscribed Guests', count: subscribedGuestsCount },
    { value: 'unit-members', label: 'Unit Members', count: unitMembersCount },
  ];

  const getGroupLabel = (group: RecipientGroup): string => {
    const option = recipientOptions.find((opt) => opt.value === group);
    return option?.label || group;
  };

  const getGroupLabelWithCount = (group: RecipientGroup, totalRecipientCount?: number): string => {
    const option = recipientOptions.find((opt) => opt.value === group);
    const label = option?.label || group;
    
    // If we have a total recipient count and only one group, show that count
    if (totalRecipientCount !== undefined) {
      return `${totalRecipientCount} ${label}`;
    }
    
    // Otherwise show the current count for this group
    const count = option?.count || 0;
    return count > 0 ? `${count} ${label}` : label;
  };

  const previewExampleRecipient = useMemo(
    () => getPreviewExampleRecipient(rsvps, sendTo, event, invoiceById),
    [rsvps, sendTo, event, invoiceById]
  );

  const paymentPreviewSample = useMemo(() => getPaymentPreviewSample(rsvps, invoiceById), [rsvps, invoiceById]);

  const previewRsvpUrl = useMemo(
    () => (unitId && eventId ? buildAnnouncementRsvpUrl(unitId, eventId) : ''),
    [unitId, eventId]
  );

  const previewRsvpDashboardUrl = useMemo(() => buildAnnouncementRsvpDashboardUrl(), []);

  const previewExampleNote = useMemo(() => {
    if (sendTo.length === 0) {
      return 'Select recipient groups so we can pick a realistic example name from your attendee list where possible.';
    }
    if (previewExampleRecipient.source === 'placeholder') {
      return 'No RSVP on file matched your selected groups for a named example; placeholder details are shown.';
    }
    return undefined;
  }, [sendTo.length, previewExampleRecipient.source]);

  const pendingRecipientsList = useMemo(
    () =>
      buildAnnouncementRecipientsList(
        sendTo,
        rsvps,
        event,
        invoiceById,
        guestList,
        unitMemberDirectory
      ),
    [sendTo, rsvps, event, invoiceById, guestList, unitMemberDirectory]
  );

  const handleViewRecipients = async (announcement: Announcement) => {
    if (!unitId || !eventId) return;

    setSelectedAnnouncement(announcement);
    setShowRecipientsDialog(true);
    setLoadingRecipients(true);

    try {
      const recipientsSnapshot = await getDocs(
        collection(
          db,
          'units',
          unitId,
          'events',
          eventId,
          'announcements',
          announcement.id,
          'recipients'
        )
      );

      const recipientsData: AnnouncementRecipient[] = [];
      recipientsSnapshot.forEach((doc) => {
        const data = doc.data();
        recipientsData.push({
          id: doc.id,
          email: data.email,
          name: data.name,
          sentAt: parseDate(data.sentAt) || new Date(),
          read: data.read || false,
          readAt: parseDate(data.readAt),
          readSource: data.readSource,
          resendEmailId: data.resendEmailId,
        });
      });

      // Sort by name
      recipientsData.sort((a, b) => a.name.localeCompare(b.name));
      setRecipients(recipientsData);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      showError('Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleSendAnnouncement = async () => {
    if (!subject.trim()) {
      showError('Please enter a subject line');
      return;
    }

    if (!message.trim()) {
      showError('Please enter a message');
      return;
    }

    if (sendTo.length === 0) {
      showError('Please select at least one recipient group');
      return;
    }

    if (!currentUser || !unitId || !eventId) {
      showError('Invalid request');
      return;
    }

    const recipientCount = getRecipientCount();
    if (recipientCount === 0) {
      showError('No recipients found');
      return;
    }

    const result = await confirmAction({
      title: 'Send Announcement?',
      text: `This will send an email to ${recipientCount} recipient(s).`,
      confirmButtonText: 'Send',
    });

    if (!result.isConfirmed) return;

    setSending(true);
    setSendProgress({ sent: 0, total: recipientCount });
    try {
      const announcementRef = await addDoc(
        collection(db, 'units', unitId, 'events', eventId, 'announcements'),
        {
          message: message.trim(),
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
          sentTo: sendTo,
          readBy: [],
          includeRsvpLink,
          includeRsvpDashboardLink,
          includePaymentLink,
        }
      );

      const sendAnnouncementEmail = httpsCallable<
        SendAnnouncementEmailPayload & { batchOffset: number; batchSize: number },
        SendAnnouncementEmailResult
      >(functions, 'sendAnnouncementEmail');
      const result = await sendAnnouncementInBatches(
        (payload) => sendAnnouncementEmail(payload),
        {
          unitId,
          eventId,
          announcementId: announcementRef.id,
          message: message.trim(),
          subject: subject.trim(),
          sendTo,
          includeRsvpLink,
          includeRsvpDashboardLink,
          includePaymentLink,
        },
        setSendProgress
      );

      const totalSent = result.totalRecipients;
      if (totalSent > 0) {
        await updateDoc(announcementRef, {
          recipientCount: totalSent,
        });
      }

      showSuccess(`Announcement sent to ${totalSent} recipient(s)!`);
      setMessage('');

      window.location.reload();
    } catch (error) {
      console.error('Error sending announcement:', error);
      showError('Failed to send announcement');
    } finally {
      setSending(false);
      setSendProgress(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 w-full min-w-0 py-6 sm:py-8 lg:py-10">
        <WideDashboardContentContainer className="max-w-6xl">
          <div className="mb-6 sm:mb-8 lg:mb-10 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="ghost" className="-ml-2 lg:-ml-3" onClick={() => navigate(`/event/${eventId}`)}>
              ← Back to Event
            </Button>
            <EventHeaderCredits unitId={unitId} />
          </div>
          <div className="mb-6 sm:mb-8 lg:mb-10">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Announcements</h1>
            <p className="mt-1 text-muted-foreground lg:mt-2 lg:max-w-2xl lg:text-base">
              Send updates and announcements to your attendees
            </p>
          </div>

          {/* Send New Announcement */}
          <Card className="mb-8 shadow-sm lg:mb-10">
            <CardHeader className="lg:pb-4">
              <CardTitle className="text-lg lg:text-xl">Send New Announcement</CardTitle>
              <CardDescription className="lg:text-base">
                Email all attendees or specific groups about event updates
              </CardDescription>
            </CardHeader>
            <CardContent className="lg:pt-2">
              <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_1fr] lg:gap-10 xl:grid-cols-[minmax(0,26rem)_1fr]">
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="sendTo">Send To</Label>
                    <MultiSelect
                      options={recipientOptions}
                      value={sendTo}
                      onChange={(value) => {
                        const groups = value as RecipientGroup[];
                        if (groups.includes('all') && !sendTo.includes('all')) {
                          setSendTo(['all']);
                        } else if (groups.includes('all') && sendTo.includes('all') && groups.length > 1) {
                          setSendTo(groups.filter((g) => g !== 'all'));
                        } else {
                          setSendTo(groups);
                        }
                      }}
                      placeholder="Select recipient groups..."
                      className="lg:mt-1.5"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Select one or more groups. Duplicate emails will be removed automatically.
                    </p>
                  </div>

                  <div className="space-y-4 rounded-lg border border-border/60 bg-muted/30 p-4 lg:p-5">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Email options
                    </p>
                    <div>
                      <Checkbox
                        id="includeRsvpLink"
                        checked={includeRsvpLink}
                        onChange={(e) => setIncludeRsvpLink(e.target.checked)}
                        label="Include a link to RSVP"
                      />
                      <p className="mt-1 text-xs text-muted-foreground ml-6">
                        Add a direct link to the event RSVP page in the email
                      </p>
                    </div>
                    <div>
                      <Checkbox
                        id="includeRsvpDashboardLink"
                        checked={includeRsvpDashboardLink}
                        onChange={(e) => setIncludeRsvpDashboardLink(e.target.checked)}
                        label="Include a link to RSVP Dashboard"
                      />
                      <p className="mt-1 text-xs text-muted-foreground ml-6">
                        Add a link to the guest dashboard (sign-in required) where they can review RSVPs
                      </p>
                    </div>
                    <div>
                      <Checkbox
                        id="includePaymentLink"
                        checked={includePaymentLink}
                        onChange={(e) => setIncludePaymentLink(e.target.checked)}
                        label="Include payment link"
                      />
                      <p className="mt-1 text-xs text-muted-foreground ml-6">
                        Include a payment link showing the recipient&apos;s current outstanding balance (if applicable)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex min-h-0 flex-col gap-4 lg:min-h-[320px]">
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Email subject line..."
                    />
                  </div>
                  <div className="flex min-h-0 flex-1 flex-col gap-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your announcement message..."
                    rows={6}
                    className="min-h-[160px] flex-1 resize-y lg:min-h-[240px]"
                  />
                  <p className="text-xs text-muted-foreground lg:text-right">{message.length} characters</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-4 border-t pt-6 lg:mt-8 lg:pt-8">
                {sending && sendProgress ? (
                  <div
                    className="flex items-center justify-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span className="text-base font-semibold tabular-nums">
                      {sendProgress.sent} / {sendProgress.total}
                    </span>
                    <span className="text-sm text-muted-foreground">emails sent</span>
                  </div>
                ) : null}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-muted-foreground">
                    <Users className="mr-1 inline h-4 w-4 align-text-bottom" />
                    Will send to {getRecipientCount()} recipient(s)
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => setShowRecipientListDialog(true)}
                      disabled={!unitId || sendTo.length === 0}
                    >
                      <ListChecks className="mr-2 h-4 w-4" />
                      Recipient list
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => setShowPreviewDialog(true)}
                      disabled={!unitId || !eventId}
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      Preview email
                    </Button>
                    <Button
                      className="w-full sm:w-auto lg:min-w-[200px]"
                      size="lg"
                      onClick={handleSendAnnouncement}
                      disabled={sending || !subject.trim() || !message.trim() || sendTo.length === 0}
                    >
                      <Send className="mr-2 h-4 w-4" />
                      {sending && sendProgress
                        ? `${sendProgress.sent} / ${sendProgress.total}`
                        : sending
                          ? 'Sending...'
                          : 'Send Announcement'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Announcement History */}
          <Card className="shadow-sm">
            <CardHeader className="lg:pb-4">
              <CardTitle className="flex items-center gap-2 text-lg lg:text-xl">
                <Clock className="h-5 w-5 shrink-0" />
                Announcement History
              </CardTitle>
              <CardDescription className="lg:text-base">
                Past announcements sent to attendees
              </CardDescription>
            </CardHeader>
            <CardContent>
              {announcements.length === 0 ? (
                <div className="py-10 text-center text-muted-foreground lg:py-14">
                  <Bell className="mx-auto h-12 w-12 mb-2 opacity-20 lg:h-14 lg:w-14" />
                  <p>No announcements sent yet</p>
                </div>
              ) : (
                <ul className="space-y-3 lg:space-y-4">
                  {announcements.map((announcement) => (
                    <li key={announcement.id}>
                      <div
                        className="cursor-pointer rounded-lg border border-border/80 bg-card p-4 transition-colors hover:bg-muted/40 lg:flex lg:gap-8 lg:p-5"
                        onClick={() => handleViewRecipients(announcement)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleViewRecipients(announcement);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="min-w-0 flex-1 space-y-3">
                          <p className="text-sm whitespace-pre-wrap break-words lg:text-base lg:leading-relaxed">
                            {announcement.message}
                          </p>
                          <div className="flex flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:gap-6">
                            <span className="inline-flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 shrink-0" />
                              {formatDate(announcement.createdAt)}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <Users className="h-3.5 w-3.5 shrink-0" />
                              Viewed in app by {announcement.readBy.length}
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between lg:mt-0 lg:w-64 lg:shrink-0 lg:flex-col lg:items-stretch lg:border-t-0 lg:border-l lg:pt-0 lg:pl-8">
                          <div className="flex flex-wrap gap-1.5 lg:justify-end">
                            {Array.isArray(announcement.sentTo) ? (
                              announcement.sentTo.length === 1 ? (
                                <Badge key={announcement.sentTo[0]} variant="outline" className="font-normal">
                                  {getGroupLabelWithCount(announcement.sentTo[0], announcement.recipientCount)}
                                </Badge>
                              ) : (
                                announcement.sentTo.map((group) => (
                                  <Badge key={group} variant="outline" className="font-normal">
                                    {getGroupLabel(group)}
                                  </Badge>
                                ))
                              )
                            ) : (
                              <Badge variant="outline" className="font-normal">
                                {getGroupLabelWithCount(announcement.sentTo as RecipientGroup, announcement.recipientCount)}
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="w-full text-xs sm:w-auto lg:w-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRecipients(announcement);
                            }}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1.5" />
                            View Recipients
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </WideDashboardContentContainer>
      </main>

      <Dialog open={showRecipientListDialog} onOpenChange={setShowRecipientListDialog}>
        <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden lg:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Recipients for this send ({pendingRecipientsList.length})</DialogTitle>
            <DialogDescription>
              Unique email addresses for your selected groups. Duplicate addresses across groups are only sent once.
            </DialogDescription>
          </DialogHeader>
          {sendTo.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">Select at least one recipient group to see who will be included.</p>
          ) : (
            <>
              {sendTo.includes('unit-members') && unitMembersDirectoryLoading ? (
                <div className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
                  <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  Loading unit member emails from the directory…
                </div>
              ) : null}
              {pendingRecipientsList.length === 0 && !(sendTo.includes('unit-members') && unitMembersDirectoryLoading) ? (
                <p className="py-4 text-sm text-muted-foreground">
                  No recipients matched yet. Check that your groups include people with email addresses (for unit members,
                  profiles must be readable from the member directory).
                </p>
              ) : pendingRecipientsList.length > 0 ? (
                <>
                  <div className="min-h-0 max-h-[min(60vh,520px)] flex-1 overflow-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[min(40%,18rem)]">Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead className="w-28 min-w-[6.5rem] whitespace-nowrap">Outstanding</TableHead>
                          <TableHead className="w-36 min-w-[8rem] whitespace-nowrap">Paid status</TableHead>
                          <TableHead className="w-32 min-w-[7rem]">Type</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingRecipientsList.map((row) => (
                          <TableRow key={row.email}>
                            <TableCell className="font-medium">{row.name}</TableCell>
                            <TableCell className="break-all text-muted-foreground">{row.email}</TableCell>
                            <TableCell className="whitespace-nowrap text-muted-foreground">
                              {row.ticketPriceFormatted ?? '–'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-muted-foreground">
                              {row.paidStatusLabel ?? '–'}
                            </TableCell>
                            <TableCell className="align-top">
                              <Badge variant="secondary" className="whitespace-nowrap font-normal">
                                {recipientSourceLabel(row.source)}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The server may still resolve some unit member emails via authentication if a profile was not available
                    here. Subscribed guests are taken from your unit guest list.
                  </p>
                </>
              ) : null}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto lg:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Email preview</DialogTitle>
            <DialogDescription>
              Example of the announcement email (subject line, greeting, and links match what Dining Steward sends).
            </DialogDescription>
          </DialogHeader>
          {unitId && eventId ? (
            <AnnouncementEmailPreview
              subjectLine={subject.trim() || defaultSubjectLine}
              recipientName={previewExampleRecipient.name}
              recipientEmail={previewExampleRecipient.email}
              exampleNote={previewExampleNote}
              eventName={event?.name || 'your event'}
              eventDateLabel={formatEventDateForAnnouncementEmail(event)}
              location={event?.location?.trim() ? event.location : 'TBC'}
              message={message}
              includeRsvpLink={includeRsvpLink}
              rsvpUrl={previewRsvpUrl}
              includeRsvpDashboardLink={includeRsvpDashboardLink}
              rsvpDashboardUrl={previewRsvpDashboardUrl}
              includePaymentLink={includePaymentLink}
              paymentSample={paymentPreviewSample}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Recipients Dialog */}
      <Dialog open={showRecipientsDialog} onOpenChange={setShowRecipientsDialog}>
        <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden lg:max-h-[80vh] lg:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Announcement Recipients</DialogTitle>
            <DialogDescription>
              View all recipients and whether they've opened the announcement email
            </DialogDescription>
          </DialogHeader>

          {selectedAnnouncement && (
            <div className="mb-4 rounded-lg border border-border/60 bg-muted/30 px-3 py-3 lg:mb-6 lg:flex lg:gap-6 lg:px-4 lg:py-4">
              <p className="min-w-0 flex-1 text-sm text-foreground lg:text-base">
                &ldquo;{selectedAnnouncement.message.substring(0, 160)}
                {selectedAnnouncement.message.length > 160 ? '…' : ''}&rdquo;
              </p>
              <p className="mt-2 shrink-0 text-xs text-muted-foreground lg:mt-0 lg:border-l lg:border-border/60 lg:pl-6 lg:text-sm">
                Sent on {formatDate(selectedAnnouncement.createdAt)}
              </p>
            </div>
          )}

          <div className="mt-2 flex-1 overflow-y-auto lg:mt-0">
            {loadingRecipients ? (
              <div className="flex items-center justify-center py-10 lg:py-14">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent lg:h-10 lg:w-10" />
              </div>
            ) : recipients.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground lg:py-14">
                <Users className="mx-auto h-12 w-12 mb-2 opacity-20 lg:h-14 lg:w-14" />
                <p>No recipient data available</p>
              </div>
            ) : (
              <div>
                <div className="mb-3 flex flex-col gap-1 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-1 lg:mb-4 lg:text-base">
                  <span>Total recipients: {recipients.length}</span>
                  <span className="hidden sm:inline" aria-hidden>
                    ·
                  </span>
                  <span>Opened: {recipients.filter((r) => r.read).length}</span>
                  <span className="hidden sm:inline" aria-hidden>
                    ·
                  </span>
                  <span>Not yet opened: {recipients.filter((r) => !r.read).length}</span>
                </div>
                <p className="mb-3 text-xs text-muted-foreground lg:mb-4">
                  &ldquo;Opened&rdquo; is detected from the recipient's email (open tracking), so it works even if
                  they never log in to the app. Some email clients block this, so a recipient may have read the
                  email without being detected.
                </p>
                <div className="grid gap-2 sm:gap-3 lg:grid-cols-2 lg:gap-3">
                  {recipients.map((recipient) => (
                    <div
                      key={recipient.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border/80 p-3 transition-colors hover:bg-muted/40 lg:p-4"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        {recipient.read ? (
                          <CheckCircle className="h-5 w-5 shrink-0 text-green-600" />
                        ) : (
                          <Circle className="h-5 w-5 shrink-0 text-muted-foreground" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{recipient.name}</p>
                          <p className="truncate text-xs text-muted-foreground">{recipient.email}</p>
                        </div>
                      </div>
                      <div className="shrink-0 text-right text-xs">
                        {recipient.read ? (
                          <span className="font-medium text-green-600">
                            Opened{recipient.readAt ? ` ${formatDate(recipient.readAt)}` : ''}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Not opened</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


